import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE, contactsAPI, outboundAPI, updateAPI, setupAPI, pipelineAPI } from '../../api/client';
import { captureEvent } from '../../lib/posthog';

import type { UpdateInfo } from '../../api/client';
import { ReviewQueuePanel } from '../ReviewQueuePanel';
import { ScheduledMessageComposer } from '../ScheduledMessageComposer';
import { ScheduledMessagesList } from '../ScheduledMessagesList';
import DataSharingSection from '../settings/DataSharingSection';
import { CollapsibleSection } from '../ui/CollapsibleSection';
import { HealthPanel } from './HealthScreen';
import { UsagePanel } from './UsageScreen';
import { DEFAULT_MODELS } from '../../constants';
import type { AIProvider, ClawerContact, ScheduledMessage } from '../../types';

const PROVIDER_OPTIONS: { value: AIProvider; label: string }[] = [
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI' },
  { value: 'anthropic', label: 'Anthropic' },
  { value: 'ollama', label: 'Ollama (local)' },
];

type UpdateStatus = 'idle' | 'checking' | 'updating' | 'restarting' | 'error';

export function SettingsScreen() {
  const [profileNotes, setProfileNotes] = useState('');
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [contacts, setContacts] = useState<ClawerContact[]>([]);
  const [scheduledRows, setScheduledRows] = useState<ScheduledMessage[]>([]);

  // Update state
  const [updateInfo, setUpdateInfo] = useState<UpdateInfo | null>(null);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus>('idle');
  const [updateError, setUpdateError] = useState('');

  // Config state
  const [cfgProvider, setCfgProvider] = useState<AIProvider>('gemini');
  const [cfgModel, setCfgModel] = useState('');
  const [cfgApiKey, setCfgApiKey] = useState('');
  const [cfgOllamaUrl, setCfgOllamaUrl] = useState('http://localhost:11434');
  const [cfgTimezone, setCfgTimezone] = useState('');
  const cfgInitial = useRef<{ provider: string; model: string; timezone: string } | null>(null);
  const [cfgUserLanguage, setCfgUserLanguage] = useState('en');
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [cfgSaveStatus, setCfgSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [cfgValidating, setCfgValidating] = useState(false);
  const [cfgValidated, setCfgValidated] = useState(false);
  const [cfgValidationError, setCfgValidationError] = useState('');

  // Contact tracking state
  const [cfgTrackingMode, setCfgTrackingMode] = useState<'opt_out' | 'opt_in'>('opt_out');
  const [bulkPhones, setBulkPhones] = useState('');
  const [bulkResult, setBulkResult] = useState<{ matched: number; not_found: string[]; updated: number } | null>(null);
  const [bulkStatus, setBulkStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [resetStatus, setResetStatus] = useState<'idle' | 'loading' | 'done'>('idle');
  const [resetResult, setResetResult] = useState<string | null>(null);

  // Reprocess state
  const [reprocessStatus, setReprocessStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [reprocessResult, setReprocessResult] = useState<string | null>(null);

  // History sync state
  const [historySyncStatus, setHistorySyncStatus] = useState<'idle' | 'loading' | 'done' | 'error'>('idle');
  const [historySyncResult, setHistorySyncResult] = useState<string | null>(null);

  // Pipeline trigger state
  const [pipelineJobs, setPipelineJobs] = useState<string[]>(['contact_sync', 'move_sync']);
  const [pipelineRunGroup, setPipelineRunGroup] = useState<string | null>(
    () => localStorage.getItem('pipelineRunGroup'),
  );
  const [pipelineStatus, setPipelineStatus] = useState<Array<{
    job_name: string;
    status: string;
    total_items: number;
    processed_items: number;
    error_message: string | null;
  }> | null>(null);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const pipelinePollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const refreshOutbound = useCallback(async () => {
    try {
      const [contactRows, scheduled] = await Promise.all([
        contactsAPI.list({ limit: 200 }),
        outboundAPI.listScheduled({ limit: 50 }),
      ]);
      setContacts(contactRows);
      setScheduledRows(scheduled);
    } catch (err) {
      console.error('Failed to refresh outbound settings:', err);
    }
  }, []);

  const checkForUpdate = useCallback(async () => {
    setUpdateStatus('checking');
    setUpdateError('');
    try {
      const info = await updateAPI.check();
      setUpdateInfo(info);
      setUpdateStatus('idle');
    } catch (err) {
      setUpdateStatus('error');
      setUpdateError(err instanceof Error ? err.message : 'Failed to check for updates');
    }
  }, []);

  useEffect(() => {
    const saved = localStorage.getItem('clawer-profile-corrections');
    if (saved) setProfileNotes(saved);
    void refreshOutbound();
    void checkForUpdate();
    void setupAPI.loadState().then(state => {
      setCfgProvider(state.aiProvider);
      setCfgModel(state.aiModel);
      setCfgApiKey(state.aiApiKey);
      setCfgOllamaUrl(state.ollamaUrl);
      setCfgTimezone(state.timezone);
      cfgInitial.current = { provider: state.aiProvider, model: state.aiModel, timezone: state.timezone };
      setCfgUserLanguage(state.userLanguage || 'en');
      setCfgTrackingMode(state.trackingMode || 'opt_out');
      setCfgLoaded(true);
    });
  }, [refreshOutbound, checkForUpdate]);

  useEffect(() => {
    if (!pipelineRunGroup) return;

    const poll = async () => {
      try {
        const res = await pipelineAPI.status(pipelineRunGroup);
        setPipelineStatus(res.jobs);

        // Stop polling when all jobs are done
        const allDone = res.jobs.every(j => j.status === 'completed' || j.status === 'error');
        if (allDone && pipelinePollingRef.current) {
          clearInterval(pipelinePollingRef.current);
          pipelinePollingRef.current = null;
          localStorage.removeItem('pipelineRunGroup');
        }
      } catch {
        // Silently retry on next interval
      }
    };

    poll(); // immediate first poll
    pipelinePollingRef.current = setInterval(poll, 3000);

    return () => {
      if (pipelinePollingRef.current) {
        clearInterval(pipelinePollingRef.current);
        pipelinePollingRef.current = null;
      }
    };
  }, [pipelineRunGroup]);

  function handleProviderChange(provider: AIProvider) {
    setCfgProvider(provider);
    setCfgModel(DEFAULT_MODELS[provider]);
    setCfgValidated(false);
    setCfgValidationError('');
  }

  function handleModeChange(mode: string) {
    if (mode === 'managed') {
      setCfgProvider('managed');
      setCfgModel(DEFAULT_MODELS['managed']);
    } else {
      setCfgProvider('gemini');
      setCfgModel(DEFAULT_MODELS['gemini']);
    }
    setCfgValidated(false);
    setCfgValidationError('');
  }

  async function handleValidateKey() {
    setCfgValidating(true);
    setCfgValidationError('');
    try {
      const body: { provider: string; api_key?: string; ollama_url?: string } = { provider: cfgProvider };
      if (cfgProvider === 'ollama') {
        body.ollama_url = cfgOllamaUrl;
      } else {
        body.api_key = cfgApiKey;
      }
      const result = await setupAPI.validateKey(body);
      setCfgValidated(result.valid);
      if (!result.valid) setCfgValidationError(result.error || 'Invalid API key');
    } catch (err) {
      setCfgValidationError(err instanceof Error ? err.message : 'Validation failed');
      setCfgValidated(false);
    } finally {
      setCfgValidating(false);
    }
  }

  async function handleSaveConfig() {
    setCfgSaveStatus('saving');
    try {
      if (cfgProvider === 'managed') {
        await setupAPI.saveState({
          aiProvider: 'managed',
          aiModel: cfgModel,
          timezone: cfgTimezone,
          userLanguage: cfgUserLanguage,
          trackingMode: cfgTrackingMode,
        });
      } else {
        await setupAPI.saveState({
          aiProvider: cfgProvider,
          aiModel: cfgModel,
          aiApiKey: cfgApiKey,
          ollamaUrl: cfgOllamaUrl,
          timezone: cfgTimezone,
          userLanguage: cfgUserLanguage,
          trackingMode: cfgTrackingMode,
        });
      }
      // Sync gateway token with provider mode
      if (cfgProvider !== 'managed') {
        // Clear stale token when switching away from managed
        localStorage.removeItem('clawer_gateway_token');
      } else if (!localStorage.getItem('clawer_gateway_token')) {
        // Restore from backend if switching back to managed and token is missing
        try {
          const state = await setupAPI.loadState();
          if (state.gatewayToken) {
            localStorage.setItem('clawer_gateway_token', state.gatewayToken);
          }
        } catch {
          // Non-blocking
        }
      }
      // Fire per-setting events for changed values
      const initial = cfgInitial.current;
      if (initial) {
        if (initial.provider !== cfgProvider) {
          captureEvent('settings_changed', { setting: 'provider' });
        }
        if (initial.model !== cfgModel) {
          captureEvent('settings_changed', { setting: 'model' });
        }
        if (initial.timezone !== cfgTimezone) {
          captureEvent('settings_changed', { setting: 'timezone' });
        }
      }
      cfgInitial.current = { provider: cfgProvider, model: cfgModel, timezone: cfgTimezone };
      setCfgSaveStatus('saved');
      setTimeout(() => setCfgSaveStatus('idle'), 2500);
    } catch {
      setCfgSaveStatus('error');
      setTimeout(() => setCfgSaveStatus('idle'), 3000);
    }
  }

  function saveNotes() {
    localStorage.setItem('clawer-profile-corrections', profileNotes);
    setSavedAt(new Date().toLocaleString());
  }

  async function applyUpdate() {
    if (!updateInfo?.latest) return;

    setUpdateStatus('updating');
    setUpdateError('');

    try {
      await updateAPI.apply(updateInfo.latest);
    } catch {
      // Expected: the updater sidecar kills the app container, dropping this
      // HTTP connection. Network errors here mean the update was triggered.
    }
    setUpdateStatus('restarting');
    pollUntilHealthy();
  }

  function pollUntilHealthy() {
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes at 2s intervals

    const interval = setInterval(async () => {
      attempts++;
      try {
        const resp = await fetch(`${API_BASE}/health`, { signal: AbortSignal.timeout(3000) });
        if (resp.ok) {
          clearInterval(interval);
          window.location.reload();
        }
      } catch {
        // App is still restarting — keep polling
      }

      if (attempts >= maxAttempts) {
        clearInterval(interval);
        setUpdateStatus('error');
        setUpdateError('App did not come back after update. Check server logs.');
      }
    }, 2000);
  }

  const currentVersion = updateInfo?.current ?? '...';

  return (
    <div className="h-full overflow-y-auto glass-scroll">
      <div className="max-w-md sm:max-w-lg md:max-w-2xl mx-auto px-4 pt-6 pb-24 space-y-6">
        <h1 className="text-2xl font-bold text-ink" data-ph-no-mask>Settings</h1>

        {/* System Health — collapsed, mounts panel on expand */}
        <CollapsibleSection title="System Health" icon="monitor_heart">
          <div className="p-4 space-y-4">
            <HealthPanel active />
          </div>
        </CollapsibleSection>

        {/* AI Usage */}
        <CollapsibleSection title="AI Usage" icon="bar_chart">
          <div className="p-4 space-y-4">
            <UsagePanel active />
          </div>
        </CollapsibleSection>

        {/* Backend Connection */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Backend Connection</h2>
          </div>
          <div className="divide-y divide-edge">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2 min-w-0">
                <span className="material-icons-round text-success text-lg shrink-0">check_circle</span>
                <span className="text-sm text-ink truncate">{localStorage.getItem('clawer_api_url') || '(default)'}</span>
              </div>
              {localStorage.getItem('clawer_api_url') && (
                <button
                  onClick={() => { localStorage.removeItem('clawer_api_url'); window.location.reload(); }}
                  className="text-xs px-3 py-1.5 rounded-lg border border-edge text-ink-secondary hover:text-error hover:border-error/30 transition-colors shrink-0 ml-3"
                  data-ph-no-mask
                >
                  Disconnect
                </button>
              )}
            </div>
          </div>
        </div>

        {/* My Language */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider">My Language</h2>
          </div>
          <div className="p-4 space-y-2">
            <p className="text-xs text-ink-secondary">Messages you write will be translated from this language.</p>
            <select
              value={cfgUserLanguage}
              onChange={(e) => setCfgUserLanguage(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
            >
              <option value="en">English</option>
              <option value="ko">Korean</option>
              <option value="es">Spanish</option>
              <option value="zh">Chinese</option>
              <option value="ja">Japanese</option>
              <option value="fr">French</option>
              <option value="de">German</option>
              <option value="pt">Portuguese</option>
              <option value="ar">Arabic</option>
              <option value="hi">Hindi</option>
              <option value="th">Thai</option>
              <option value="vi">Vietnamese</option>
              <option value="id">Indonesian</option>
            </select>
          </div>
        </div>

        {/* Configuration */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>AI Configuration</h2>
          </div>
          {!cfgLoaded ? (
            <div className="p-4 text-sm text-ink-tertiary" data-ph-no-mask>Loading...</div>
          ) : (
            <div className="p-4 space-y-4">
              {/* AI Mode */}
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>AI Mode</label>
                <select
                  value={cfgProvider === 'managed' ? 'managed' : 'byok'}
                  onChange={(e) => handleModeChange(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                >
                  <option value="byok">Bring Your Own Key (BYOK)</option>
                  <option value="managed">Clawer AI (Subscription)</option>
                </select>
              </div>

              {cfgProvider === 'managed' ? (
                <div className="space-y-4">
                  {/* Auto-configured */}
                  <div className="p-4 rounded-lg bg-canvas border border-edge">
                    <div className="flex items-center gap-2">
                      <span className="material-icons-round text-success text-lg">check_circle</span>
                      <div>
                        <p className="text-sm font-medium text-ink">Auto-configured</p>
                        <p className="text-xs text-ink-secondary">
                          Your AI provider is managed by your Clawer subscription.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>Model</label>
                    <select
                      value={cfgModel}
                      onChange={(e) => setCfgModel(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                    >
                      <option value="gemini-3-flash-preview">Gemini 3 Flash (1 credit)</option>
                      <option value="gpt-5.4">GPT-5.4 (3 credits)</option>
                      <option value="gemini-3.1-pro-preview">Gemini 3.1 Pro (5 credits)</option>
                    </select>
                    <p className="text-xs text-ink-tertiary mt-1">
                      Different models cost different credits. See usage dashboard.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Provider */}
                  <div>
                    <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>Provider</label>
                    <select
                      value={cfgProvider}
                      onChange={(e) => handleProviderChange(e.target.value as AIProvider)}
                      className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                    >
                      {PROVIDER_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Model */}
                  <div>
                    <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>Model</label>
                    <input
                      type="text"
                      value={cfgModel}
                      onChange={(e) => { setCfgModel(e.target.value); setCfgValidated(false); }}
                      placeholder="e.g. gemini-2.5-flash"
                      className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                    />
                  </div>

                  {/* API Key / Ollama URL */}
                  {cfgProvider === 'ollama' ? (
                    <div>
                      <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>Ollama URL</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={cfgOllamaUrl}
                          onChange={(e) => { setCfgOllamaUrl(e.target.value); setCfgValidated(false); }}
                          className="flex-1 px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                        />
                        <button
                          onClick={() => void handleValidateKey()}
                          disabled={cfgValidating}
                          className="px-3 py-2 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors disabled:opacity-50"
                        >
                          {cfgValidating ? <span className="material-icons-round text-sm animate-spin">sync</span> : 'Test'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>API Key</label>
                      <div className="flex gap-2">
                        <input
                          type="password"
                          value={cfgApiKey}
                          onChange={(e) => { setCfgApiKey(e.target.value); setCfgValidated(false); setCfgValidationError(''); }}
                          placeholder="Enter API key"
                          className="flex-1 px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                        />
                        <button
                          onClick={() => void handleValidateKey()}
                          disabled={cfgValidating || !cfgApiKey}
                          className="px-3 py-2 rounded-lg border border-edge text-sm font-medium text-ink hover:bg-canvas transition-colors disabled:opacity-50"
                        >
                          {cfgValidating ? <span className="material-icons-round text-sm animate-spin">sync</span> : 'Validate'}
                        </button>
                      </div>
                      {cfgValidated && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1" data-ph-no-mask>
                          <span className="material-icons-round text-xs">check_circle</span>
                          API key is valid
                        </p>
                      )}
                      {cfgValidationError && (
                        <p className="text-xs text-red-500 mt-1">{cfgValidationError}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Timezone */}
              <div>
                <label className="block text-xs font-medium text-ink-secondary mb-1" data-ph-no-mask>Timezone</label>
                <input
                  type="text"
                  value={cfgTimezone}
                  onChange={(e) => setCfgTimezone(e.target.value)}
                  placeholder="e.g. Asia/Kuala_Lumpur"
                  className="w-full px-3 py-2 rounded-lg border border-edge text-sm focus:outline-none focus:ring-2 focus:ring-accent/20"
                />
              </div>

              {/* Save button */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => void handleSaveConfig()}
                  disabled={cfgSaveStatus === 'saving'}
                  className="px-4 py-2 rounded-lg bg-ink text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                  data-ph-no-mask
                >
                  {cfgSaveStatus === 'saving' ? 'Saving…' : 'Save changes'}
                </button>
                {cfgSaveStatus === 'saved' && (
                  <span className="text-xs text-green-600 flex items-center gap-1" data-ph-no-mask>
                    <span className="material-icons-round text-xs">check_circle</span>
                    Saved
                  </span>
                )}
                {cfgSaveStatus === 'error' && (
                  <span className="text-xs text-red-500" data-ph-no-mask>Failed to save</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Data Sharing */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Data Sharing</h2>
          </div>
          <div className="p-4">
            <DataSharingSection />
          </div>
        </div>

        <ScheduledMessageComposer contacts={contacts} userLanguage={cfgUserLanguage} onCreated={() => { void refreshOutbound(); }} />
        <ScheduledMessagesList rows={scheduledRows} onChanged={() => { void refreshOutbound(); }} />
        <ReviewQueuePanel onChanged={() => { void refreshOutbound(); }} />

        {/* Software Update */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Software Update</h2>
          </div>
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink" data-ph-no-mask>Current version</span>
              <span className="text-sm font-mono font-medium text-ink-secondary">{currentVersion}</span>
            </div>

            {updateStatus === 'checking' && (
              <div className="flex items-center gap-2 text-sm text-ink-secondary" data-ph-no-mask>
                <span className="material-icons-round animate-spin text-base">sync</span>
                Checking for updates...
              </div>
            )}

            {updateStatus === 'idle' && updateInfo && !updateInfo.update_available && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-green-600" data-ph-no-mask>
                  <span className="material-icons-round text-base">check_circle</span>
                  You're up to date
                </div>
                <button
                  onClick={() => void checkForUpdate()}
                  className="text-xs text-ink-tertiary hover:text-ink transition-colors"
                  data-ph-no-mask
                >
                  Check again
                </button>
              </div>
            )}

            {updateStatus === 'idle' && updateInfo?.update_available && (
              <>
                <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="material-icons-round text-blue-600 text-base">system_update</span>
                    <span className="text-sm font-medium text-blue-800" data-ph-no-mask>
                      Version {updateInfo.latest} is available
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => void applyUpdate()}
                  className="w-full px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors"
                  data-ph-no-mask
                >
                  Update to {updateInfo.latest}
                </button>
              </>
            )}

            {updateStatus === 'updating' && (
              <div className="flex items-center gap-2 text-sm text-amber-600" data-ph-no-mask>
                <span className="material-icons-round animate-spin text-base">sync</span>
                Pulling new version...
              </div>
            )}

            {updateStatus === 'restarting' && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                <div className="flex items-center gap-2 text-sm text-amber-700" data-ph-no-mask>
                  <span className="material-icons-round animate-spin text-base">sync</span>
                  Restarting... this page will reload automatically.
                </div>
              </div>
            )}

            {updateStatus === 'error' && (
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-2">
                <div className="flex items-center gap-2 text-sm text-red-700">
                  <span className="material-icons-round text-base">error</span>
                  {updateError}
                </div>
                <button
                  onClick={() => { setUpdateStatus('idle'); setUpdateError(''); }}
                  className="text-xs text-red-600 hover:text-red-800 underline"
                  data-ph-no-mask
                >
                  Dismiss
                </button>
              </div>
            )}
          </div>
        </div>

        {/* About */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>About</h2>
          </div>
          <div className="divide-y divide-edge">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink" data-ph-no-mask>App</span>
              <span className="text-sm font-medium text-ink" data-ph-no-mask>Clawer Dashboard</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink" data-ph-no-mask>Version</span>
              <span className="text-sm font-mono font-medium text-ink-secondary">{currentVersion}</span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-ink" data-ph-no-mask>Platform</span>
              <span className="text-sm font-medium text-ink-secondary" data-ph-no-mask>Self-hosted</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Profile Corrections</h2>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-sm text-ink-secondary" data-ph-no-mask>
              Add corrections to your behavioral profile assumptions. These notes help interpret `data/user/profile.md` before Phase 8 automation.
            </p>
            <textarea
              value={profileNotes}
              onChange={(e) => setProfileNotes(e.target.value)}
              rows={5}
              placeholder="Example: I prefer follow-ups on weekends for family contacts..."
              className="w-full px-3 py-2 rounded-lg border border-edge focus:outline-none focus:ring-2 focus:ring-accent/20"
            />
            <div className="flex items-center justify-between">
              <button onClick={saveNotes} className="px-4 py-2 rounded-lg bg-ink text-white text-sm hover:opacity-90" data-ph-no-mask>
                Save notes
              </button>
              {savedAt && <span className="text-xs text-ink-tertiary">Saved {savedAt}</span>}
            </div>
          </div>
        </div>

        {/* Contact Tracking */}
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Contact Tracking</h2>
          </div>
          <div className="p-4 space-y-4">
            <div>
              <label className="block text-xs font-medium text-ink-secondary mb-2" data-ph-no-mask>Default tracking mode</label>
              <div className="space-y-2">
                <button
                  type="button"
                  onClick={() => setCfgTrackingMode('opt_out')}
                  className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all ${cfgTrackingMode === 'opt_out' ? 'border-accent bg-accent/5' : 'border-edge hover:border-ink-tertiary'}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${cfgTrackingMode === 'opt_out' ? 'border-accent' : 'border-ink-tertiary'}`}>
                      {cfgTrackingMode === 'opt_out' && <div className="w-2 h-2 rounded-full bg-accent" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-ink" data-ph-no-mask>Opt-out</span>
                      <span className="ml-2 text-xs text-accent font-medium" data-ph-no-mask>Recommended</span>
                      <p className="text-xs text-ink-tertiary mt-0.5" data-ph-no-mask>Track all contacts by default</p>
                    </div>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => setCfgTrackingMode('opt_in')}
                  className={`w-full text-left rounded-lg border-2 px-3 py-2.5 transition-all ${cfgTrackingMode === 'opt_in' ? 'border-accent bg-accent/5' : 'border-edge hover:border-ink-tertiary'}`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${cfgTrackingMode === 'opt_in' ? 'border-accent' : 'border-ink-tertiary'}`}>
                      {cfgTrackingMode === 'opt_in' && <div className="w-2 h-2 rounded-full bg-accent" />}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-ink" data-ph-no-mask>Opt-in</span>
                      <p className="text-xs text-ink-tertiary mt-0.5" data-ph-no-mask>Only track contacts you explicitly add</p>
                    </div>
                  </div>
                </button>
              </div>
              <p className="text-xs text-ink-tertiary mt-2" data-ph-no-mask>Changes apply to contacts following global policy.</p>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-edge">
              <div>
                <p className="text-sm text-ink" data-ph-no-mask>Reset All Overrides</p>
                <p className="text-xs text-ink-tertiary" data-ph-no-mask>Clear all per-contact overrides. Everyone follows global policy.</p>
              </div>
              <button
                onClick={async () => {
                  setResetStatus('loading');
                  try {
                    const res = await contactsAPI.resetAllTracking();
                    setResetResult(`${res.updated} contacts reset`);
                    setResetStatus('done');
                  } catch {
                    setResetResult('Failed');
                    setResetStatus('idle');
                  }
                }}
                disabled={resetStatus === 'loading'}
                className="text-xs px-3 py-1.5 rounded-lg bg-accent text-white disabled:opacity-50"
                data-ph-no-mask
              >
                {resetStatus === 'loading' ? 'Resetting...' : 'Reset All'}
              </button>
            </div>
            {resetResult && <p className="text-xs text-green-600">{resetResult}</p>}
            <div className="pt-3 border-t border-edge space-y-2">
              <div>
                <p className="text-sm text-ink" data-ph-no-mask>Bulk Track / Untrack</p>
                <p className="text-xs text-ink-tertiary" data-ph-no-mask>Paste phone numbers (comma, space, or newline separated)</p>
              </div>
              <textarea
                value={bulkPhones}
                onChange={(e) => setBulkPhones(e.target.value)}
                placeholder="+60176031533, +60189001377..."
                rows={3}
                className="w-full text-xs rounded-lg border border-edge px-3 py-2 font-mono"
              />
              <div className="flex gap-2">
                <button
                  onClick={async () => {
                    setBulkStatus('loading');
                    try {
                      const phones = bulkPhones.split(/[,\s\n]+/).filter(Boolean);
                      const res = await contactsAPI.bulkTrack(phones);
                      setBulkResult(res);
                      setBulkStatus('done');
                    } catch {
                      setBulkStatus('error');
                    }
                  }}
                  disabled={bulkStatus === 'loading' || !bulkPhones.trim()}
                  className="text-xs px-3 py-1.5 rounded-lg bg-green-600 text-white disabled:opacity-50"
                  data-ph-no-mask
                >
                  Track These
                </button>
                <button
                  onClick={async () => {
                    setBulkStatus('loading');
                    try {
                      const phones = bulkPhones.split(/[,\s\n]+/).filter(Boolean);
                      const res = await contactsAPI.bulkUntrack(phones);
                      setBulkResult(res);
                      setBulkStatus('done');
                    } catch {
                      setBulkStatus('error');
                    }
                  }}
                  disabled={bulkStatus === 'loading' || !bulkPhones.trim()}
                  className="text-xs px-3 py-1.5 rounded-lg bg-red-500 text-white disabled:opacity-50"
                  data-ph-no-mask
                >
                  Untrack These
                </button>
              </div>
              {bulkResult && (
                <div className="text-xs space-y-1">
                  <p className="text-green-600">{bulkResult.matched} matched, {bulkResult.updated} updated</p>
                  {bulkResult.not_found.length > 0 && (
                    <p className="text-amber-600">Not found: {bulkResult.not_found.join(', ')}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>Data</h2>
          </div>
          <div className="divide-y divide-edge">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-ink-secondary text-lg">sync</span>
                <div>
                  <p className="text-sm text-ink" data-ph-no-mask>Reprocess All Contacts</p>
                  <p className="text-xs text-ink-tertiary" data-ph-no-mask>Re-run AI profiles for every tracked contact</p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setReprocessStatus('loading');
                  setReprocessResult(null);
                  try {
                    const res = await contactsAPI.reprocess();
                    setReprocessResult(res.message);
                    setReprocessStatus('done');
                  } catch (err) {
                    setReprocessResult(err instanceof Error ? err.message : 'Failed');
                    setReprocessStatus('error');
                  }
                }}
                disabled={reprocessStatus === 'loading'}
                className="text-xs px-3 py-1.5 rounded-lg bg-accent text-white disabled:opacity-50"
                data-ph-no-mask
              >
                {reprocessStatus === 'loading' ? 'Queuing…' : 'Reprocess'}
              </button>
            </div>
            {reprocessResult && (
              <p className={`px-4 py-2 text-xs ${reprocessStatus === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                {reprocessResult}
              </p>
            )}
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-ink-secondary text-lg">history</span>
                <div>
                  <p className="text-sm text-ink" data-ph-no-mask>Sync WhatsApp History</p>
                  <p className="text-xs text-ink-tertiary" data-ph-no-mask>Pull full message history from WuzAPI into Clawer</p>
                </div>
              </div>
              <button
                onClick={async () => {
                  setHistorySyncStatus('loading');
                  setHistorySyncResult(null);
                  try {
                    const res = await contactsAPI.historySync();
                    setHistorySyncResult(`${res.ingested} messages from ${res.chats} chats`);
                    setHistorySyncStatus('done');
                  } catch (err) {
                    setHistorySyncResult(err instanceof Error ? err.message : 'Failed');
                    setHistorySyncStatus('error');
                  }
                }}
                disabled={historySyncStatus === 'loading'}
                className="text-xs px-3 py-1.5 rounded-lg bg-accent text-white disabled:opacity-50"
                data-ph-no-mask
              >
                {historySyncStatus === 'loading' ? 'Syncing…' : 'Sync History'}
              </button>
            </div>
            {historySyncResult && (
              <p className={`px-4 py-2 text-xs ${historySyncStatus === 'error' ? 'text-red-500' : 'text-green-600'}`}>
                {historySyncResult}
              </p>
            )}
          </div>
        </div>

        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider" data-ph-no-mask>AI Pipeline</h2>
          </div>
          <div className="divide-y divide-edge">
            {/* Job checkboxes */}
            <div className="px-4 py-3">
              <div className="flex items-center gap-2 mb-2">
                <span className="material-icons-round text-ink-secondary text-lg">smart_toy</span>
                <div>
                  <p className="text-sm text-ink" data-ph-no-mask>Run AI Pipeline Now</p>
                  <p className="text-xs text-ink-tertiary" data-ph-no-mask>Immediately run selected pipelines</p>
                </div>
              </div>
              <div className="flex items-center gap-4 ml-7 mb-3">
                <label className="flex items-center gap-1.5 text-xs text-ink-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pipelineJobs.includes('contact_sync')}
                    onChange={(e) => {
                      setPipelineJobs(prev =>
                        e.target.checked
                          ? [...prev, 'contact_sync']
                          : prev.filter(j => j !== 'contact_sync')
                      );
                    }}
                    className="rounded border-edge"
                  />
                  Contact Sync
                </label>
                <label className="flex items-center gap-1.5 text-xs text-ink-secondary cursor-pointer">
                  <input
                    type="checkbox"
                    checked={pipelineJobs.includes('move_sync')}
                    onChange={(e) => {
                      setPipelineJobs(prev =>
                        e.target.checked
                          ? [...prev, 'move_sync']
                          : prev.filter(j => j !== 'move_sync')
                      );
                    }}
                    className="rounded border-edge"
                  />
                  Move Sync
                </label>
              </div>
              <button
                onClick={async () => {
                  if (pipelineJobs.length === 0) return;
                  setPipelineError(null);
                  setPipelineStatus(null);
                  try {
                    const res = await pipelineAPI.run(pipelineJobs);
                    localStorage.setItem('pipelineRunGroup', res.run_group);
                    setPipelineRunGroup(res.run_group);
                    setPipelineStatus(res.jobs.map(j => ({ ...j, total_items: 0, processed_items: 0, error_message: null })));
                  } catch (err) {
                    setPipelineError(err instanceof Error ? err.message : 'Failed to start pipeline');
                  }
                }}
                disabled={pipelineJobs.length === 0 || (pipelineStatus !== null && !pipelineStatus.every(j => j.status === 'completed' || j.status === 'error'))}
                className="text-xs px-3 py-1.5 rounded-lg bg-accent text-white disabled:opacity-50 ml-7"
                data-ph-no-mask
              >
                {pipelineStatus && !pipelineStatus.every(j => j.status === 'completed' || j.status === 'error')
                  ? 'Running…'
                  : 'Run Now'}
              </button>
            </div>

            {/* Status display */}
            {pipelineStatus && (
              <div className="px-4 py-3 space-y-2">
                {pipelineStatus.map((job) => (
                  <div key={job.job_name} className="flex items-center gap-2 text-xs">
                    <span className={`material-icons-round text-sm ${
                      job.status === 'completed' ? 'text-green-500' :
                      job.status === 'error' ? 'text-red-500' :
                      job.status === 'running' ? 'text-blue-500 animate-spin' :
                      'text-ink-tertiary'
                    }`}>
                      {job.status === 'completed' ? 'check_circle' :
                       job.status === 'error' ? 'error' :
                       job.status === 'running' ? 'sync' :
                       'schedule'}
                    </span>
                    <span className="text-ink-secondary">
                      {job.job_name === 'contact_sync' ? 'Contact Sync' : 'Move Sync'}
                    </span>
                    <span className="text-ink-tertiary">
                      {job.status === 'running' && job.total_items > 0
                        ? `${job.processed_items}/${job.total_items}`
                        : job.status === 'completed' && job.total_items > 0
                        ? `${job.total_items}/${job.total_items}`
                        : job.status}
                    </span>
                    {job.error_message && (
                      <span className="text-red-500 truncate max-w-[200px]" title={job.error_message}>
                        {job.error_message}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}

            {pipelineError && (
              <p className="px-4 py-2 text-xs text-red-500">{pipelineError}</p>
            )}
          </div>
        </div>

        <p className="text-xs text-ink-tertiary text-center" data-ph-no-mask>
          Clawer keeps your relationship context local. Outbound automation is safety-gated.
        </p>
      </div>
    </div>
  );
}

export default SettingsScreen;
